import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Form, message, Row, Space, Spin, Typography } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { formatString } from "../../../../../utils/formatString";
import { InputField, ReactSelectField, TextAreaField } from "../../../../atoms/FormElement";
import { getRequiredErrorMessage } from "../../../../libs/form";
import { formatDateByType } from "../../../../libs/utils";
import { getConfigData } from "../../../BookingForm/ducks/actions";
import { usePendingRequestsActions } from "../../../PendingApprovalRequets/api/useRefreshPendingRequests";
import {
  bouncedReasons,
  defaultFormVal,
  formattedBankList,
  paymentModeRendering,
  removeEmptyValues,
  visibilityOptions,
  paymentModeOptions,
} from "../../utili";
import {
  type DepositDestinationOption,
  depositDestinationSelectComponents,
  getDepositToNameForPayload,
} from "../ModifyPaymentStatus/depositDestinationOptions";
import { useBeneficiaryDepositOptions } from "../ModifyPaymentStatus/hook/useBeneficiaryDepositOptions";
import { useInitializePaymentData } from "../ModifyPaymentStatus/hook/useInitializePaymentData";
import { useTotalOverpaymentAmount } from "../ModifyPaymentStatus/hook/useTotalOverpaymentAmount";
import {
  type ReceiptPaymentDetailsPayload,
  type UpdateAndIssueReceiptPayload,
  useUpdateAndIssueReceipt,
} from "./api/useUpdateAndIssueReceipt";
import ReceiptMultiInvoiceAllocation from "./index";
import { buildReceiptAllocations } from "./utils";

const { Paragraph } = Typography;

type ReceivePaymentMultiInvoiceProps = {
  data: any;
  organization: any;
  onCancel: () => void;
  onUpdate?: () => void;
};

const depositDestinationSelectStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 44,
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    textTransform: "none",
    paddingTop: 2,
    paddingBottom: 2,
  }),
  option: (base: Record<string, unknown>, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...base,
    textTransform: "none",
    paddingTop: 4,
    paddingBottom: 4,
    minHeight: 50,
    display: "flex",
    alignItems: "center",
    ...(state.isSelected
      ? { backgroundColor: "#767676", color: "#ffffff" }
      : state.isFocused
        ? { backgroundColor: "#DEEBFF", color: "#262626" }
        : { backgroundColor: "transparent", color: "#262626" }),
  }),
};

const ReceivePaymentMultiInvoice = (props: ReceivePaymentMultiInvoiceProps) => {
  const { data, organization } = props;
  const dispatch: any = useDispatch();

  const [depositDate, setDepositDate] = useState(
    data?.params?.data?.dueDate ? dayjs(data?.params?.data?.dueDate) : dayjs(),
  );
  const { totalOverpaymentRemaining } = useTotalOverpaymentAmount(data?.params?.data?.tenantId);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loader, setLoader] = useState(false);
  const [isOverPaymentCredit, setIsOverPaymentCredit] = useState(false);
  const [bankList, setBankList] = useState<any[]>([]);
  const [depositTo, setDepositTo] = useState<string | null>(null);
  const [allocationInvoices, setAllocationInvoices] = useState(() => (data?.params?.data ? [data.params.data] : []));
  const [receiptTotalAmount, setReceiptTotalAmount] = useState(() => Number(data?.params?.data?.balance) || 0);
  const [isAllocationValid, setIsAllocationValid] = useState(true);

  const configData = useSelector((state: any) => state.dashboard.configData);

  const bouncedChequeFee = organization?.bouncedChequeFee?.amount ?? 0;
  const bounceVat = organization?.bouncedChequeFee?.vatInPercentage || 0;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    clearErrors,
  } = useForm({ defaultValues: defaultFormVal });

  const selectedPaymentMode = useWatch({ control, name: "paymentModeType" });
  const isChequePayment = selectedPaymentMode?.value === "cheque";
  const isBankTransferPayment = selectedPaymentMode?.value === "bank_transfer";
  const shouldRequireDepositDate = isChequePayment || isBankTransferPayment;
  const overPaymentCreditValue: number | null =
    totalOverpaymentRemaining && totalOverpaymentRemaining > 0 ? totalOverpaymentRemaining : null;

  const twoStepReceiptConfirmation = organization?.twoStepReceiptConfirmation;
  const { refreshPendingRequests } = usePendingRequestsActions();
  const { updateAndIssueReceipt, isUpdatingAndIssuingReceipt } = useUpdateAndIssueReceipt();

  useEffect(() => {
    if (data?.params?.data?.pdcId) {
      setAllocationInvoices([data.params.data]);
      setReceiptTotalAmount(Number(data.params.data?.balance) || 0);
    }
  }, [data?.params?.data?.pdcId]);

  const pendingInvoicesSubtitle = useMemo(() => {
    const row = data?.params?.data;
    const tenantName = row?.tenantDetails?.tenantNameEN;
    const propertyName = row?.propertyDetails?.propertyName;
    const unitCode = row?.propertyDetails?.unitCode;
    const propertyContext = [propertyName, unitCode].filter(Boolean).join(" ");
    const context = [tenantName, propertyContext].filter(Boolean).join(" · ");
    return context ? `Other pending invoices for ${context}` : "Other pending invoices";
  }, [data?.params?.data]);

  const onBack = () => {
    reset();
    props.onCancel();
  };

  useInitializePaymentData({
    data,
    onEditBack: false,
    setValue,
    setPaymentStatusOn: () => {},
    setPaymentMode: () => {},
    setBankNameFromDropDown: () => {},
    bouncedChequeFee,
    visibilityOptions,
    paymentModeOptions,
    bouncedReasons,
    formatString,
    bounceVat,
  });

  const ownerId = data?.params?.data?.ownerId ?? null;
  const { beneficiaryDepositOptions, beneficiaryOptionsLoading } = useBeneficiaryDepositOptions(ownerId);

  useEffect(() => {
    setBankList(formattedBankList(configData));
  }, [configData?.bankList]);

  useEffect(() => {
    if (!configData?.bankList) {
      dispatch(getConfigData(["bankList"]));
    }
    return () => reset();
  }, []);

  useEffect(() => {
    if (!beneficiaryDepositOptions.length || !data?.params?.data) return;
    const row = data.params.data;
    const acc = String(row?.beneficiaryAccount ?? row?.depositToAccountNumber ?? "").trim();
    const bank = String(row?.beneficiaryBank ?? row?.depositToBank ?? "").trim();
    const name = String(row?.depositTo ?? row?.beneficiaryName ?? "").trim();
    const match =
      beneficiaryDepositOptions.find(
        (o) => o.accountNumber && acc && o.accountNumber === acc && (!bank || o.bankName === bank),
      ) || beneficiaryDepositOptions.find((o) => name && (o.depositToName === name || o.label === name));
    if (match) {
      setValue("depositTo", match, { shouldValidate: false });
      setValue("depositBank", match.bankName ?? "", { shouldValidate: false });
      setValue("depositAccount", match.accountNumber ?? "", { shouldValidate: false });
      setValue("branch", match.branch ?? "", { shouldValidate: false });
      setValue("swiftCode", match.swiftCode ?? "", { shouldValidate: false });
      setValue("address", match.address ?? "", { shouldValidate: false });
      setDepositTo(getDepositToNameForPayload(match));
    }
  }, [beneficiaryDepositOptions, data?.params?.data, setValue]);

  const onDepositDestinationChange = (e: DepositDestinationOption | null) => {
    if (!e) return;
    setDepositTo(getDepositToNameForPayload(e));
    setValue("depositTo", e);
    setValue("depositBank", e.bankName ?? "");
    setValue("depositAccount", e.accountNumber ?? "");
    setValue("branch", e.branch ?? "");
    setValue("swiftCode", e.swiftCode ?? "");
    setValue("address", e.address ?? "");
  };

  useEffect(() => {
    if (!isChequePayment) {
      clearErrors(["paymentRef", "bankName", "depositTo"]);
    }
  }, [isChequePayment, clearErrors]);

  const validatePaymentFields = (values: any) => {
    if (!values?.paymentModeType?.value) {
      setError("paymentModeType", { type: "manual", message: getRequiredErrorMessage() });
      return false;
    }

    if (values?.paymentModeType?.value !== "cheque") return true;

    if (isChequePayment) {
      if (!values?.paymentRef?.trim()) {
        setError("paymentRef", { type: "manual", message: getRequiredErrorMessage() });
        return false;
      }
      if (!values?.bankName?.value) {
        setError("bankName", { type: "manual", message: getRequiredErrorMessage() });
        return false;
      }
      if (!values?.depositTo?.value) {
        setError("depositTo", { type: "manual", message: getRequiredErrorMessage() });
        return false;
      }
    }

    if (shouldRequireDepositDate && !depositDate) {
      message.error("Deposit Date is required");
      return false;
    }
    return true;
  };

  const appendDepositDestinationDetails = (
    paymentDetails: ReceiptPaymentDetailsPayload,
    values: any,
    paymentStatus: string,
  ) => {
    const payableTo = data?.params?.data?.payableTo;
    const depositToPayloadName = depositTo || getDepositToNameForPayload(values?.depositTo);
    const depositIban = values?.depositTo?.beneficiaryIBAN ?? data?.params?.data?.beneficiaryIBAN;

    if (!depositToPayloadName && !values?.depositTo?.value) return;

    if (payableTo === "owner" && paymentStatus === "paid") {
      Object.assign(paymentDetails, {
        depositTo: depositToPayloadName || values?.depositTo?.value,
        depositToAccountNumber: values?.depositAccount,
        depositToBank: values?.depositBank,
        depositToIBAN: depositIban,
        branch: values?.branch,
        swiftCode: values?.swiftCode,
        address: values?.address,
      });
    } else if (payableTo === "lessor" && paymentStatus === "paid") {
      Object.assign(paymentDetails, {
        depositTo: depositToPayloadName || values?.depositTo?.value,
        lessorAccountNumber: values?.depositAccount,
        lessorBankName: values?.depositBank,
        depositToIBAN: depositIban,
        branch: values?.branch,
        swiftCode: values?.swiftCode,
        address: values?.address,
      });
    }else if(paymentStatus === "paid"){
       Object.assign(paymentDetails, {
        depositTo: depositToPayloadName || values?.depositTo?.value,
        lessorAccountNumber: values?.depositAccount,
        lessorBankName: values?.depositBank,
        depositToIBAN: depositIban,
        branch: values?.branch,
        swiftCode: values?.swiftCode,
        address: values?.address,
      });
    
    }
  };

  const buildSubmitPayload = (values: any): UpdateAndIssueReceiptPayload => {
    const leaseId = data?.params?.data?.leaseId;
    const allocations = buildReceiptAllocations(allocationInvoices);
    const paymentMode = values?.paymentModeType?.value;
    const paymentStatus = values?.paymentStatus?.value ?? "paid";

    const payload: UpdateAndIssueReceiptPayload = {
      leaseId,
      receiptAmount: Number(receiptTotalAmount) || 0,
      applyOverpaymentCredit: isOverPaymentCredit,
      allocations,
    };

    if (!isOverPaymentCredit && paymentMode) {
      const paymentDetails: ReceiptPaymentDetailsPayload = {
        paymentMode,
      };

      if (values?.paymentRef?.trim()) {
        paymentDetails.paymentRef = values.paymentRef.trim();
      }

      if (values?.paymentNote?.trim()) {
        paymentDetails.note = values.paymentNote.trim();
      }

      if (paymentMode === "cheque" || paymentMode === "bank_transfer") {
        paymentDetails.depositDate = formatDateByType(depositDate, "api");
      }

      if (paymentMode === "cheque") {
        paymentDetails.bankName = values?.bankName?.label;
        appendDepositDestinationDetails(paymentDetails, values, paymentStatus);
      }

      payload.paymentDetails = removeEmptyValues(paymentDetails);
    }

    return removeEmptyValues(payload);
  };

  const submitUpdate = async (values: any) => {
    if (!isAllocationValid) return;
    if (!isOverPaymentCredit && !validatePaymentFields(values)) return;

    const leaseId = data?.params?.data?.leaseId;
  
    if (!leaseId) {
      message.error("Lease ID is missing");
      return;
    }

    setLoader(true);

    try {
      const response = await updateAndIssueReceipt(buildSubmitPayload(values));

      if (response?.success !== false) {
        message.success(response?.message || "Receipt issued successfully");
        if (twoStepReceiptConfirmation) {
          await refreshPendingRequests();
        }
        props.onUpdate?.();
      } else {
        const errorMsg = response?.error || response?.message || "Something went wrong";
        message.error(errorMsg);
        setErrorMessage(errorMsg);
        setIsError(true);
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.error || error?.response?.data?.message || "Something went wrong";
      message.error(errorMsg);
      setErrorMessage(errorMsg);
      setIsError(true);
    } finally {
      setLoader(false);
    }
  };

  const submitLabel = isOverPaymentCredit ? "Apply Credit" : "Update & Issue Receipt";
  const isSubmitting = loader || isUpdatingAndIssuingReceipt;

  return (
    <Card>
      {isSubmitting && !isError && (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Space size={24} className="w-100 text-center" direction="vertical">
              <Spin size="large" className="ag-fontSize32" />
            </Space>
          </Col>
        </Row>
      )}

      <Form layout="vertical" className="bf-main payment-form" onFinish={handleSubmit(submitUpdate)}>
        {!isError ? (
          <>
            <Row gutter={24}>
              <Col span={24}>
                <ReceiptMultiInvoiceAllocation
                  invoices={allocationInvoices}
                  receiptTotalAmount={receiptTotalAmount}
                  availableOverpaymentCredit={overPaymentCreditValue}
                  tenantName={data?.params?.data?.tenantDetails?.tenantNameEN}
                  applyOverpaymentCredit={isOverPaymentCredit}
                  onApplyOverpaymentCreditChange={setIsOverPaymentCredit}
                  onReceiptTotalChange={setReceiptTotalAmount}
                  leaseId={data?.params?.data?.leaseId}
                  tenantId={data?.params?.data?.tenantId}
                  onInvoicesChange={setAllocationInvoices}
                  onAllocationValidityChange={setIsAllocationValid}
                  pendingInvoicesSubtitle={pendingInvoicesSubtitle}
                />
              </Col>

              {!isOverPaymentCredit && (
                <>
                  <Col span={12}>
                    <ReactSelectField
                      fieldname="paymentModeType"
                      isRequired
                      label="Payment Mode"
                      rules={{ required: getRequiredErrorMessage() }}
                      validate={errors.paymentModeType && "error"}
                      validMessage={errors.paymentModeType?.message}
                      control={control}
                      iProps={{ placeholder: "Select payment mode" }}
                      selectOption={paymentModeRendering(data)}
                    />
                  </Col>

                  <Col span={12}>
                    <InputField
                      fieldname="paymentRef"
                      label="Payment Ref"
                      isRequired={isChequePayment}
                      control={control}
                      iProps={{ placeholder: "Enter Payment Ref" }}
                      rules={{ required: isChequePayment ? getRequiredErrorMessage() : false }}
                      validate={errors.paymentRef && "error"}
                      validMessage={errors.paymentRef?.message}
                    />
                  </Col>

                  {shouldRequireDepositDate && (
                    <>
                      <Col span={12}>
                        <Form.Item label="Deposit Date" required>
                          <DatePicker
                            style={{ width: "100%" }}
                            value={depositDate}
                            format="DD-MM-YYYY"
                            size="middle"
                            picker="date"
                            clearIcon={false}
                            onChange={(date) => date && setDepositDate(dayjs(date))}
                          />
                        </Form.Item>
                      </Col>

                      {isChequePayment && (
                        <>
                          <Col span={12}>
                            <ReactSelectField
                              fieldname="bankName"
                              isRequired
                              label="Bank Name"
                              rules={{ required: getRequiredErrorMessage() }}
                              validate={errors.bankName && "error"}
                              validMessage={errors.bankName?.message}
                              control={control}
                              placeholder="Enter Bank Name"
                              selectOption={bankList}
                            />
                          </Col>

                          <Col span={24}>
                            <ReactSelectField
                              fieldname="depositTo"
                              label="Deposit To"
                              control={control}
                              setValue={setValue}
                              style={{ width: "100%" }}
                              components={depositDestinationSelectComponents}
                              onChange={onDepositDestinationChange}
                              isRequired
                              rules={{ required: getRequiredErrorMessage() }}
                              validate={errors.depositTo && "error"}
                              validMessage={errors.depositTo?.message}
                              iProps={{
                                placeholder: "Select deposit destination",
                                isLoading: beneficiaryOptionsLoading,
                                styles: depositDestinationSelectStyles,
                              }}
                              selectOption={beneficiaryDepositOptions}
                            />
                          </Col>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
              <Col span={24}>
                <TextAreaField
                  label="Note"
                  fieldname="paymentNote"
                  iProps={{ placeholder: "Note...." }}
                  setValue={setValue}
                  control={control}
                  valueGot={data?.params?.data?.paymentNote}
                />
              </Col>
            </Row>

            <Row gutter={24} justify="space-between">
              <Col>
                <Button className="ef-discard-btn" onClick={onBack}>
                  Back
                </Button>
              </Col>
              <Col>
                <Button
                  disabled={isSubmitting || !isAllocationValid}
                  type="primary"
                  className="ef-approve-btn"
                  htmlType="submit"
                >
                  {isSubmitting ? "Loading..." : submitLabel}
                </Button>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Space size={24} className="w-100 text-center" direction="vertical">
                  <ExclamationCircleOutlined className="ag-fontSize32 ag-error" />
                  <Paragraph className="ag-error fontWeight600 mb-0">{errorMessage}</Paragraph>
                </Space>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={24}>
                <Button className="ef-discard-btn" htmlType="button" onClick={() => setIsError(false)}>
                  Back
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Card>
  );
};

export default React.memo(ReceivePaymentMultiInvoice);
